import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { BookOpen, Lightbulb, Baby, GraduationCap, ChevronDown } from 'lucide-react';

const categories = {
  'Fiction': {
    slug: 'fiction',
    icon: BookOpen,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    subcategories: [
      { name: 'General Fiction', slug: 'general-fiction' },
      { name: 'Romance & Drama', slug: 'romance-drama' },
      { name: 'Fantasy/Thriller', slug: 'fantasy-thriller' },
      { name: 'Poetry', slug: 'poetry' },
      { name: 'Comics', slug: 'comics' },
    ]
  },
  'Non-Fiction': {
    slug: 'non-fiction',
    icon: Lightbulb,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    subcategories: [
      { name: 'Biographies & Memoirs', slug: 'biographies-memoirs' },
      { name: 'Self-Help & Personal Growth', slug: 'self-help-growth' },
      { name: 'History & Politics', slug: 'history-politics' },
      { name: 'Science, Business & Lifestyle', slug: 'science-business-lifestyle' },
    ]
  },
  'Kids': {
    slug: 'children-young-readers',
    icon: Baby,
    color: 'text-pink-500',
    bgColor: 'bg-pink-50',
    subcategories: [
      { name: 'Picture Books (0–8)', slug: 'picture-books' },
      { name: 'Middle Grade (8–12)', slug: 'middle-grade' },
      { name: 'Young Adult (YA)', slug: 'young-adult' },
    ]
  },
  'Academic': {
    slug: 'academic-reference',
    icon: GraduationCap,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    subcategories: [
      { name: 'School & College Textbooks', slug: 'textbooks' },
      { name: 'Exam Prep & Study Guides', slug: 'exam-prep' },
      { name: 'Reference', slug: 'reference' },
    ]
  }
};

interface CategoryMenuProps {
  className?: string;
  isMobile?: boolean;
}

const CategoryMenu: React.FC<CategoryMenuProps> = ({ className, isMobile = false }) => {
  const [openCategories, setOpenCategories] = React.useState<string[]>([]);
  const navigate = useNavigate();

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  if (isMobile) {
    return (
      <div className={cn("space-y-2", className)}>
        {Object.entries(categories).map(([categoryName, categoryData]) => {
          const Icon = categoryData.icon;
          const isOpen = openCategories.includes(categoryName);
          
          return (
            <Collapsible
              key={categoryName}
              open={isOpen}
              onOpenChange={() => toggleCategory(categoryName)}
            >
              <div className="flex items-center w-full rounded-lg hover:bg-muted/50 transition-colors">
                <Link
                  to={`/category/${categoryData.slug}`}
                  className="flex items-center gap-3 flex-1 p-3"
                  aria-label={`Browse all ${categoryName} books`}
                >
                  <div className={cn("p-2 rounded-lg", categoryData.bgColor)}>
                    <Icon className={cn("h-4 w-4", categoryData.color)} />
                  </div>
                  <span className="font-medium text-foreground">{categoryName}</span>
                </Link>
                <CollapsibleTrigger
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${categoryName} subcategories`}
                  className="p-3"
                >
                  <ChevronDown 
                    className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform duration-200",
                      isOpen && "rotate-180"
                    )} 
                  />
                </CollapsibleTrigger>
              </div>
              
              <CollapsibleContent className="animate-accordion-down">
                <div className="ml-12 mt-1 space-y-1 pb-2">
                  <Link
                    to={`/category/${categoryData.slug}`}
                    className="block py-2 px-3 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors"
                  >
                    All {categoryName}
                  </Link>
                  {categoryData.subcategories.map((sub) => (
                    <Link
                      key={sub.slug}
                      to={`/category/${sub.slug}`}
                      className="block py-2 px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
                    >
                      {sub.name}
                    </Link>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        
        {/* Browse All Link */}
        <Link
          to="/books"
          className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-orange-500/10 to-primary/10 hover:from-orange-500/20 hover:to-primary/20 transition-colors mt-4"
        >
          <div className="p-2 rounded-lg bg-gradient-to-r from-orange-500 to-primary">
            <BookOpen className="h-4 w-4 text-white" />
          </div>
          <span className="font-bold text-foreground">Browse All Books</span>
        </Link>
      </div>
    );
  }

  return (
    <NavigationMenu className={className}>
      <NavigationMenuList className="gap-1">
        {Object.entries(categories).map(([categoryName, categoryData]) => {
          const Icon = categoryData.icon;
          
          return (
            <NavigationMenuItem key={categoryName}>
              <NavigationMenuTrigger
                onClick={() => navigate(`/category/${categoryData.slug}`)}
                className="h-auto py-2 px-3 text-sm font-medium text-foreground/80 hover:text-foreground bg-transparent hover:bg-muted/50 data-[state=open]:bg-muted/50"
              >
                <Icon className={cn("h-4 w-4 mr-1.5", categoryData.color)} />
                {categoryName}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="w-[400px] p-4 md:w-[500px] lg:w-[550px]">
                  {/* Header Card */}
                  <NavigationMenuLink asChild>
                    <Link
                      to={`/category/${categoryData.slug}`}
                      className={cn(
                        "flex items-center gap-4 p-4 rounded-xl mb-3 transition-all hover:scale-[1.02]",
                        categoryData.bgColor
                      )}
                    >
                      <div className={cn("p-3 rounded-xl bg-white/80 shadow-sm")}>
                        <Icon className={cn("h-6 w-6", categoryData.color)} />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">{categoryName}</div>
                        <p className="text-sm font-bold text-foreground">
                          Browse all {categoryName.toLowerCase()} books
                        </p>
                      </div>
                    </Link>
                  </NavigationMenuLink>
                  
                  {/* Subcategories Grid */}
                  <div className="grid grid-cols-2 gap-1">
                    {categoryData.subcategories.map((subcategory) => (
                      <NavigationMenuLink asChild key={subcategory.slug}>
                        <Link
                          to={`/category/${subcategory.slug}`}
                          className="block p-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        >
                          {subcategory.name}
                        </Link>
                      </NavigationMenuLink>
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default CategoryMenu;
export { categories };
